
import { Component, Vue, Watch } from 'vue-property-decorator';
import LogRocket from 'logrocket';
import { isEmbedded, redirectExternal } from '@/utils/shopifyBridge';
import {
  INSTALLATION_ROUTE_NAMES,
  isInstallationRoute,
  isOnboardExitRoute,
} from '@/config/installationRoutes';
import { attemptShopifySessionAuth, authenticateWithSessionToken } from '@/services/shopifySessionAuth';
import { shouldBypassOnboardGuard } from '@/utils/onboardPreview';
import { mapActions, mapGetters } from 'vuex';

import {
  get, getIdentityToken, setIdentityToken, removeIdentityToken,
  revokeIdentitySession, verifyIdentityToken, onSessionEvicted, isSessionEvicted,
} from '@/services/http';
import { EventHandler } from '@/modules/events';
import { MixpanelInitializer } from '@/modules/mixpanelInitializer';
import { runShellGuard } from '@/utils/shellGuard';
import mixpanel from 'mixpanel-browser';
import { EventBus } from '@/main';
// Same module instance as the `EventBus` above — main.ts only re-exports it.
import { LOGOUT_REQUESTED } from '@/eventBus';

import { PermissionModel } from '@/models/permission';
import { StoreModel } from '@/models/store';
import {UserModel} from '@/models/user';
import { DimensionColumnList } from '@/collections/dimension_columns';
import { TagTypeList } from '@/collections/tag_types';
import { ReportMetadataList } from '@/collections/report_metadatas';
import { TagList } from '@/collections/tags';
import { MeasureList } from '@/collections/measures';
import { LoginRequestList } from '@/collections/login_requests';
import { LoginRequestModel } from '@/models/login_request';
import Alert from '@/components/Alert.vue';
import VerticalMenuBar from '@/components/VerticalMenuBar.vue';
import AppBanner from '@/components/AppBanner.vue';
import CompanionAppBanner from '@/components/companion-app-banner/CompanionAppBanner.vue';
import { isDemoStore } from '@/components/companion-app-banner/companionAppBannerRules';
import AtlasOptInDialog from '@/components/AtlasOptInDialog.vue';
import { showOptInBanner } from '@/services/frontendAppOptIn';
import { liveChatState } from '@/services/crispLiveChat';
import { cancelHandoffDelivery } from '@/services/crispDelivery';
import PurchaseListDrawer from '@/components/purchase-order/PurchaseListDrawer.vue';
import PurchaseListIcon from '@/components/purchase-order/PurchaseListIcon.vue';
import { providePermission } from '@/services/PermissionService';

/**
 * AS-11619: routes that must never be re-entered on the merchant's behalf.
 * Re-opening /subscription/confirm?charge_id=… after a re-login re-confirms
 * the charge (the reconfirmation loop that got the per-tab route persistence
 * in created() removed); the OAuth callbacks carry one-shot codes.
 */
function isTransactionalRoute(path: string): boolean {
  // `path` is $route.path: no query string, so no `?` case (same shape as
  // http.ts's isAllowedWhenSignedOut).
  return ['/subscription/confirm', '/oauth2callback', '/slack_callback']
    .some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

@Component({
  components: {
    SideBarHorizon: () => import('@/components/SideBarHorizon.vue'),
    Login: () => import('@/components/Login.vue'),
    UpgradeVersion: () => import('@/components/UpgradeVersion.vue'),
    Permission: () => import('@/components/Permission.vue'),
    DataPermissionNotice: () => import('@/components/DataPermissionNotice.vue'),
    DataSyncWarning: () => import('@/components/DataSyncWarning.vue'),
    Installing: () => import('@/components/Installing.vue'),
    HorizontalMenu: () => import('@/components/HorizontalMenu.vue'),
    LoadingSlider: () => import('@/components/LoadingSlider.vue'),
    AgentChatFab: () => import('@/components/common/AgentChatFAB.vue'),
    LoginRequestPopup: () => import('@/components/LoginRequestPopup.vue'),
    ReviewRequestDialog: () => import('@/components/ReviewRequestDialog.vue'),
    Alert,
    VerticalMenuBar,
    AppBanner,
    CompanionAppBanner,
    AtlasOptInDialog,
    PurchaseListDrawer,
    PurchaseListIcon,
  },
  provide() {
    const vm = this as any;
    return {
      store: vm.store,
    };
  },
})

export default class App extends Vue {

  public get getAppMenuBarSize() {
    if (this.isExpandAppMenuBar) {
      return 'view-show-app-menu-bar';
    } else {
      return 'view-hide-app-menu-bar';
    }
  }

  public get isEmbeddedMode() {
    return isEmbedded();
  }

  public get isPassedInstallation() {
    return this.store.passedInstallingFlow !== 'new';
  }

  public get isConfirmingSubscription() {
    return [
      'subscriptionConfirm',
      'autoSubscription',
    ].includes(
      this.$route.name || '',
    );
  }

  public get isWelcomeMode(): boolean {
    return false;
  }

  public get getCanShowUpgradeVersion() {
    const requireUpgradePages = [
      'purchase-order-detail',
    ];

    if (!this.$route.name) {
      return false;
    }

    return requireUpgradePages.includes(this.$route.name);
  }

  public get isAssistantRoute(): boolean {
    return this.$route.path.startsWith('/assistant');
  }

  // 2026-08-25: the Atlas onboarding embed (/assistant/onboarding) has its
  // own progress rail + readiness checklist. Stacking AppBanner's sync
  // state on top duplicated that signal with an independently-polled
  // percentage that visibly disagreed with Atlas's (live: warbler 60% vs
  // atlas 90% at the same moment) and ate vertical space above the embed.
  // Gate the whole <app-banner>, not just its sync span: AppBanner's
  // template is a v-if/v-else-if/v-else chain, so hiding only the sync
  // state would fall through to the v-else Calendly "book a call" button
  // (or other promos) during onboarding — worse than the redundancy being
  // removed. None of the banner's states belong above the guided rail.
  public get isAtlasOnboardingRoute(): boolean {
    return this.$route.path.startsWith('/assistant/onboarding');
  }

  // Companion app banner (docs/features/companion-app-banner, W6): hidden where
  // <app-banner> is and on the shared demo store.
  public get showCompanionBanner(): boolean {
    return !this.isAtlasOnboardingRoute
      && !isDemoStore(this.store.shopName, window.location.hostname);
  }

  // AS-11621 (M6): the opt-in invitation, moved here from Home.vue (which redirects
  // to /assistant/today on menu V3 before anyone sees it); <app-banner> renders it.
  public showAtlasOptIn: boolean = false;
  /**
   * The invitation's ✕: closed for this page load (back on the next one). Kept here, not in
   * <app-banner>, which unmounts on the atlas onboarding route and would forget it.
   */
  public optInDismissed: boolean = false;

  public get showOptInBanner(): boolean {
    return showOptInBanner(this.store.frontendAppOptInAvailable, this.isAtlasOnboardingRoute);
  }

  public isLogined: boolean = false;
  public isRequiredLogin: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public installing: boolean = false;
  public checkInstalling: boolean = false;
  public isCallbackRequest: boolean = false;
  public creating: boolean = false;
  public errorMessage: string = '';
  public initialRouteFailed: boolean = false;
  public shopifyName: string = '';
  public isDataReady: boolean = true;
  public needUpgrade: boolean = false;
  public permission: PermissionModel = new PermissionModel();
  public isPermissionLoading: boolean = true;
  public currentFeature: string = '';
  public store: StoreModel = new StoreModel();
  public isLoggedInFromHades: boolean = false;
  public installationUrls: string[] = INSTALLATION_ROUTE_NAMES;

  public app: any = null; // deprecated: kept temporarily for child component prop compatibility
  // private tabId: string = ''; // disabled: per-tab route persistence removed
  public reportMetadatas: ReportMetadataList = new ReportMetadataList();
  public tagTypes: TagTypeList = new TagTypeList();
  public tags: TagList = new TagList();
  public measures: MeasureList = new MeasureList();
  public contentLoading: boolean = false;
  public showFullscreenPopup: boolean = true;
  public isFullLoadFinished: boolean = false;
  public mainRandomKey: number = Math.random();
  public storageSupported: boolean = true;
  public isExpandAppMenuBar: boolean = true;
  public isCallbackGoogle: boolean = false;
  public appSnackbar: boolean = false;
  public appSnackbarTimeout: number = 5000;
  public appSnackbarMessage: string = '';
  public appSnackbarColor: string = '';
  public appSnackbarAction: { label: string; to?: string } | null = null;
  public referenceRoute: any = {};
  public activeDimensions: DimensionColumnList = new DimensionColumnList();
  public appContentMargin: string = '56px';
  private permissionService: any;
  private permissionServiceInitialized: boolean = false;
  private permissionInitTimeout: number | null = null;
  private olapsWarmedThisAppInstance: boolean = false; // Add this line
  public pendingLoginRequest: LoginRequestModel | null = null;
  public showLoginRequestPopup: boolean = false;
  public isChatboxSplitActive: boolean = false;
  public showDataReadyModal: boolean = false;
  public isPurchaseListDrawerOpen: boolean = false;
  private athenaPollingInterval: number | null = null;
  // AS-11619: unsubscribe from http.ts's session-evicted notification.
  private offSessionEvicted: (() => void) | null = null;

  public async created() {
    Vue.prototype.$permissionsInitializing = true;

    EventBus.$on('purchase-list-drawer-visible', this.onPurchaseListDrawerVisible);

    // AS-10880: surfaces that cannot `$emit('logout')` up to App — the Atlas
    // settings sidebar, which reaches us through AtlasEmbed inside
    // `<router-view>` — raise this instead. `doLogout` is safe unbound here:
    // Vue 2's initMethods binds every `methods` entry to the component, and
    // vue-class-component routes class methods through `options.methods`.
    EventBus.$on(LOGOUT_REQUESTED, this.doLogout);

    // AS-11619: http.ts raises this once per document when it has evicted the
    // session (see onSessionEvicted). Subscribed before the first await so a
    // boot-time eviction is not missed; the handler is a no-op while signed out.
    this.offSessionEvicted = onSessionEvicted(this.onSessionEvicted);

    // Per-tab route persistence (DISABLED): reload restores last route in
    // THIS tab, other tabs land on root. sessionStorage survives reload and
    // is naturally scoped per tab, so we use it to mint a stable tab id.
    // Removed because /subscription/confirm?charge_id=... could be restored
    // after a successful confirmation, creating a reconfirmation loop.
    // try {
    //   this.tabId = sessionStorage.getItem('warbler_tab_id') || '';
    //   if (!this.tabId) {
    //     this.tabId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    //     sessionStorage.setItem('warbler_tab_id', this.tabId);
    //   }
    //   if (isEmbedded() && this.$route.path === '/') {
    //     const saved = localStorage.getItem(`warbler_last_route_${this.tabId}`);
    //     const isTransactional = saved && (
    //       saved.startsWith('/subscription/confirm') ||
    //       saved.startsWith('/oauth2callback') ||
    //       saved.startsWith('/slack_callback')
    //     );
    //     if (saved && saved !== '/' && saved !== this.$route.fullPath && !isTransactional) {
    //       this.$router.replace(saved).catch((err: any) => {
    //         if (err?.name !== 'NavigationDuplicated') throw err;
    //       });
    //     }
    //   }
    // } catch {
    //   // no-op: storage may be blocked
    // }

    // PHASE 1: Initialize Mixpanel SDK immediately (before any async operations)
    // This ensures the SDK is ready for tracking in child components like SubscriptionConfirm
    this.initializeMixpanelSdk();

    this.clearCache();

    // Everything below reads `$route` (the shop/hmac the login + install checks
    // need), so wait for the router's INITIAL navigation first. A lazy route --
    // /assistant/* loads AtlasEmbed with import() -- resolves asynchronously, and
    // until it does `$route` is still the START route `/` with an EMPTY query.
    // Opening the app on such a URL (App Bridge mirrors /assistant/today into the
    // admin URL, so every reload and reopen does) then skipped the
    // shopify_store-vs-?shop check in checkLogin and kept the PREVIOUS store's
    // token -- another store's data under this store's admin -- or, with no
    // token, sent GET /shopify without the hmac and got a 400.
    // A FAILED first navigation (e.g. the route chunk did not load) leaves
    // `$route` on that same empty START route, so carrying on would reopen the
    // hole above. Stop instead -- no login, no install check -- and replace the
    // loader with a reload prompt so the merchant is not left on a spinner.
    const initialRouteReady = await new Promise<boolean>((resolve) => {
      this.$router.onReady(() => resolve(true), () => resolve(false));
    });
    if (!initialRouteReady) {
      this.initialRouteFailed = true;
      return;
    }

    try {
      if (!isEmbedded()) {
        if (!this.isLogined) {
          try {
            // Auto-login for demo environment or Cloudflare tunnel previews
            const isDemoHost = window.location.hostname === 'demo.assisty.ai';
            const isTestHost = window.location.hostname === 'test.assisty.ai';
            // const isPreviewHost = window.location.hostname.endsWith('.trycloudflare.com');
            const isPreviewHost = false

            if (isDemoHost) {
              const user: UserModel = new UserModel();
              await user.loginDemo();
              this.$emit('logged-in');
              localStorage.setItem('current_loggedin_email', user.email);
            } else if (isTestHost || isPreviewHost) {
              const user: UserModel = new UserModel();
              await user.loginTest();
              this.$emit('logged-in');
              localStorage.setItem('current_loggedin_email', user.email);
            }
          } catch (e: any) {
            // console.log(e);
          }
        }

        const refererUrl = document.referrer;
        if (refererUrl) {
          const urlObj = new URL(refererUrl);
          const embeddedParam = urlObj.searchParams.get('embedded');
          if (embeddedParam === '1') {
            const idToken = urlObj.searchParams.get('id_token');
            if (idToken) {
              // Managed-install migration: same endpoint as the embedded
              // bootstrap (signature-verified server-side) — replaces the
              // legacy /shopify/identify call.
              if (await authenticateWithSessionToken(idToken)) {
                this.isLogined = true;
                const query = Object.assign({}, this.$route.query);
                this.$router.replace({ query });
                await this.getStoreDetail();
                this.error = false;
              }
            }
          }
        }
      }
    } catch (e) {
      // skipped
    }

    if (this.$route.query.state && this.$route.query.code && this.$route.query.scope && this.$route.query.scope.includes('drive.file')) {
      this.isCallbackGoogle = true;
      let paramCode = `${this.$route.query.code}`;
      paramCode = paramCode.replaceAll('/', 'assisty_slash');
      const redirectUriEmbedded = `https://admin.shopify.com/store/${this.$route.query.state}/apps/assisty/oauth2callback?code=${paramCode}`;
      if (isEmbedded()) {
        redirectExternal(redirectUriEmbedded);
      } else {
        // @ts-ignore: Unreachable code error
        window.location.assign(redirectUriEmbedded);
      }
    }
    try {
      this.getAppMenuBarStatus();
      localStorage.getItem('language');
    } catch (e) {
      this.storageSupported = false;
      return;
    }

    this.$i18n.locale = 'en';
    this.isLoggedInFromHades = localStorage.getItem('loggedInFromHades') === 'true';
    if (process.env.VUE_APP_ENVIRONMENT === 'production' && !this.isLoggedInFromHades && window.location.hostname !== 'demo.assisty.ai' && !window.location.hostname.endsWith('.trycloudflare.com')) {
      try {
        LogRocket.init('igdczq/assisty');
      } catch {
        // skipped
      }
    }
    this.loading = true;
    this.checkIsRequiredLogin();
    // App Bridge CDN auto-initialises via the <script> tag in index.html
    // and exposes the `shopify` global. No manual createApp() needed.
    if (this.isCallbackRequest) {
      await this.createStore();
    } else {
      if (this.isRequiredLogin) {
        if (!this.isLogined && this.$route.path !== '/') {
          localStorage.setItem('redirect_uri', this.$route.fullPath);
        }
        await this.fullLoad();
        EventBus.$on('current-package-changed', (storeModel: StoreModel) => {
          // Assign the store data passed from the event payload
          // Store is already fetched before emission by updateStoreAndEmitPackageChange()
          this.store = storeModel;
          // Store the Shopify store name for use in other components
          if (this.store.shopName) {
            localStorage.setItem('shopifyStoreName', this.store.shopName);
          }
        });
      } else {
        this.isFullLoadFinished = true;
      }
      /* await this.permission.fetch(); */
    }
    this.setupCrisp(null);
    if (this.store && this.store.id) {
      this.initializeMixpanel();
    } else {
      EventBus.$once('store-detail-fetched', () => this.initializeMixpanel());
    }
    this.loading = false;
    EventBus.$on('update-new-store-data', async () => {
      const storeModel = new StoreModel();
      await storeModel.fetch();
      this.store = storeModel;
      // Store the Shopify store name for use in other components
      if (this.store.shopName) {
        localStorage.setItem('shopifyStoreName', this.store.shopName);
      }
      EventBus.$emit('update-new-store-data-completed');
    });
    EventBus.$on('full-load-app', async () => {
      this.mainRandomKey = Math.random();
    });
    EventBus.$on('check-athena-finish-interval', async () => {
      this.checkAthenaFinishInterval();
    });

    // Add these for new logic
    EventBus.$on('vertical-menu-pinned', this.handleVerticalMenuPinned);
    this.checkAthenaFinishInterval();

    // Pause App.vue polling when DataImporting has its own faster polling
    EventBus.$on('etl-polling-started', () => {
      if (this.athenaPollingInterval) {
        clearInterval(this.athenaPollingInterval);
        this.athenaPollingInterval = null;
      }
    });
    EventBus.$on('etl-polling-stopped', () => {
      this.checkAthenaFinishInterval();
    });

    EventBus.$on('redirect-to-google-auth', async () => {
      const scope = 'https://www.googleapis.com/auth/drive.file';
      const clientId = process.env.VUE_APP_GOOGLE_AUTH_CLIENT_ID;
      const redirectUrl = `${process.env.VUE_APP_HOST}/oauth2callback`;
      let url = '';
      if (isEmbedded()) {
        const state = this.store.shopName.split('.')[0];
        url = `https://accounts.google.com/o/oauth2/auth?state=${state}&scope=${scope}&response_type=code&access_type=offline&prompt=consent&redirect_uri=${redirectUrl}&client_id=${clientId}`;
      } else {
        url = `https://accounts.google.com/o/oauth2/auth?scope=${scope}&response_type=code&access_type=offline&prompt=consent&redirect_uri=${redirectUrl}&client_id=${clientId}`;
      }
      redirectExternal(url);
    });

    EventBus.$on('show-snackbar', async (config: any) => {
      this.appSnackbar = true;
      if (config && config.message) {
        this.appSnackbarMessage = config.message;
      } else if (config && config.text) {
        this.appSnackbarMessage = config.text;
      } else {
        this.appSnackbarMessage = config;
      }
      if (config && config.color) {
        this.appSnackbarColor = config.color;
      } else {
        this.appSnackbarColor = 'deep_sku_blue';
      }
      this.appSnackbarAction = config && config.action ? config.action : null;
    });

    EventBus.$on('clear-cache', async () => {
      this.clearCache();
    });

    // Listen for chatbox layout changes (split/overlay mode)
    EventBus.$on('app:chatbox-layout-change', this.onChatboxLayoutChange);

    // Load permissions on app startup
    if (this.$store.getters.isAuthenticated) {
      await this.permissionService.loadPermissions();
    }

    // Listen for login/logout events
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'setUser') {
        this.permissionService.loadPermissions();
      } else if (mutation.type === 'logout') {
        this.permissionService.clearCache();
      }
    });

    // Initialize permissions when app is created (if user is already logged in)
    if (this.isLogined && this.store && this.store.id) {
     await this.initializePermissionService();
    }

    // Add this in the created() method to listen for permission events
    EventBus.$on('permission-denied', (data: { feature: string, route: string }) => {
      this.isPermissionLoading = false;
      this.currentFeature = data.feature;
    });

    // Add this after initializing the permission service
    EventBus.$on('check-permission', async (data: { permission: string, route: any }) => {
      // Ensure permission service is initialized
      if (!this.permissionServiceInitialized && this.store && this.store.id) {
        await this.initializePermissionService();
      }

      // Check permission using our service
      if (this.permissionService) {
        const hasPermission = this.permissionService.hasPermission(data.permission);
        if (!hasPermission) {
          this.isPermissionLoading = false;
          this.currentFeature = data.permission;
          EventBus.$emit('permission-denied', {
            feature: data.permission,
            route: data.route.path,
          });
        }
      }
    });
  }

  // Method to handle vertical menu pinning state
  public handleVerticalMenuPinned(isPinned: boolean) {
    this.appContentMargin = isPinned ? '300px' : '56px';
    localStorage.setItem('isPinMenu', isPinned ? 'true' : 'false');
  }

  // Method to handle chatbox layout changes (split-view mode)
  public onChatboxLayoutChange(payload: { active: boolean; mode: string }) {
    this.isChatboxSplitActive = payload.active && payload.mode === 'split';
  }

  public getAppMenuBarStatus() {
    if (localStorage.getItem('isExpandAppMenuBar') === 'false') {
      this.isExpandAppMenuBar = false;
    } else {
      this.isExpandAppMenuBar = true;
    }
  }


  public changeAppMenuBarStatus(isExpand: boolean) {
    this.isExpandAppMenuBar = isExpand;
  }
  // AS-11619: the athena poller belongs to the logged-in state. Stop it the
  // moment the app leaves that state (eviction, logout, a failed boot verify);
  // without this a signed-out tab kept polling `store.fetch()` with no
  // identity — 3 × 401 a minute, forever. The START is not here: `isLogined`
  // flips true before the store is hydrated on the hades, /token/verify and
  // /shopify install paths, and a poller armed on an empty StoreModel is never
  // cleared for a `passedInstallingFlow === 'new'` store. fullLoad() starts it.
  @Watch('isLogined')
  public onIsLoginedChanged(isLogined: boolean) {
    if (isLogined) {
      return;
    }
    if (this.athenaPollingInterval) {
      clearInterval(this.athenaPollingInterval);
      this.athenaPollingInterval = null;
    }
  }

  public checkAthenaFinishInterval() {
    // AS-11619: never on a signed-out page — the poller has no identity to
    // fetch with. An empty StoreModel passes both guards below, so the login
    // screen used to start it.
    if (!this.isLogined) {
      return;
    }
    if (this.store.passedInstallingFlow === 'new') {
      return;
    }
    if (!this.store.isAthenaFinished) {
      // Clear any existing interval to prevent stacking
      if (this.athenaPollingInterval) {
        clearInterval(this.athenaPollingInterval);
        this.athenaPollingInterval = null;
      }
      localStorage.setItem('isAthenaFinished', 'true');
      this.athenaPollingInterval = window.setInterval(() => {
        if (this.store.isAthenaFinished) {
          clearInterval(this.athenaPollingInterval!);
          this.athenaPollingInterval = null;
          localStorage.setItem('isAthenaFinished', 'false');
        } else {
          this.store.fetch();
        }
      }, 60000);
    }
  }

  public async fullLoad() {
    this.isFullLoadFinished = false;
    try {
      await this.checkInstallation();
      this.isPermissionLoading = false;
      if (this.isLogined) {
        this.contentLoading = true;
        const preload = [];
        preload.push(this.tagTypes.fetch());
        if (this.activeDimensions.size() === 0) {
          preload.push(this.activeDimensions.fetch());
        }

        if (this.tags.size() === 0) {
          preload.push(this.tags.fetch());
        }
        if (this.reportMetadatas.size() === 0) {
          preload.push(this.reportMetadatas.fetch());
        }
        Promise.all(preload);
        if (this.measures.size() === 0) {
          await this.measures.fetch({ isOlap: true });
        }
        this.contentLoading = false;

        // Start checking for pending login requests
        this.startLoginRequestCheck();

        // AS-11619: the athena poller starts here, the one place every login
        // runs through with the store already hydrated — boot, the Login form
        // (`@logged-in="fullLoad"`), the embedded session exchange and the
        // hmac install all resolve inside checkInstallation() above.
        this.checkAthenaFinishInterval();
      }
    } catch (e: any) {
      // AS-11619: a preload rejected because the session was evicted while it
      // was in flight is not an error here — onSessionEvicted() has already
      // moved the app to the login state. Letting it escape would abort the
      // rest of created() (Crisp, Mixpanel, the EventBus subscriptions), and
      // the Login form's later `@logged-in="fullLoad"` never registers those.
      // Two shapes: a request short-circuited locally (SESSION_EVICTED), and
      // the very request whose 401 triggered the eviction, which rejects with
      // the raw axios error — only the state tells that one apart. Anything
      // else still surfaces.
      if (e?.code !== 'SESSION_EVICTED' && !isSessionEvicted()) {
        throw e;
      }
    } finally {
      // AS-11619: an eviction while a preload is in flight rejects it locally
      // and throws out of here; the launch gate must still come down, or the
      // merchant is left on "Launching Assisty" with no login screen behind it.
      this.isFullLoadFinished = true;
    }
  }

  /**
   * AS-11647: the next person in this tab must not inherit the way back to
   * this merchant's support chat, nor receive a summary still waiting for an
   * idle Crisp. Both ways out of a session call it: logout and eviction.
   */
  private clearLiveChatSession(): void {
    liveChatState.forget();
    cancelHandoffDelivery();
  }

  /**
   * A fresh Crisp session for whoever logs in next: Crisp keeps the previous
   * merchant's conversation and session data in this browser (MR !2880
   * review). Only for a session that was really logged in.
   */
  private resetCrispSession(): void {
    try {
      const crisp = (window as any).$crisp;
      if (typeof crisp?.push === 'function') crisp.push(['do', 'session:reset']);
    } catch {
      // a broken widget cannot hold up logout
    }
  }

  public async doLogout() {
    // Revoke the remember-me session server-side before clearing local tokens.
    await revokeIdentitySession();
    removeIdentityToken();
    // Reset Mixpanel on logout to clear user data
    MixpanelInitializer.reset();
    this.clearLiveChatSession();
    this.resetCrispSession();
    // AS-11619: leave the logged-in state here, not in checkLogin()'s catch.
    // Its /token/verify goes out with no bearer and earns a 401, which (outside
    // the admin) evicts; with isLogined still true, onSessionEvicted() would
    // keep the page the user logged out from as the redirect_uri for the NEXT
    // login — possibly another user's or store's.
    this.isLogined = false;
    this.checkLogin();
  }

  /**
   * AS-11619: http.ts has evicted the session — the refresh token was
   * rejected, the embedded renewal failed, or a refresh failed again right
   * after a reload for the same reason. Leave the logged-in state ONCE: the
   * authenticated tree unmounts, so Home cannot re-mount and re-fire its
   * requests into the dead session, and the pollers stop. http.ts pushes `/`
   * right after this handler returns (listeners run first, so `$route` is
   * still the page the session died on); navigating here as well would
   * re-create the loop this fixes. That route is kept so the re-login lands
   * back there (checkLogin reads redirect_uri). Every launch-gate flag comes
   * down too: an eviction in the middle of fullLoad() must end on the login
   * screen, not on "Launching Assisty".
   */
  public onSessionEvicted() {
    // Before the guard: an eviction at boot (token verification, isLogined
    // still false) must drop the handoff flags restored from sessionStorage too.
    this.clearLiveChatSession();
    if (!this.isLogined) {
      return;
    }
    this.resetCrispSession();
    if (this.$route.path !== '/' && !isTransactionalRoute(this.$route.path)) {
      localStorage.setItem('redirect_uri', this.$route.fullPath);
    }
    this.isLogined = false;
    this.loading = false;
    this.contentLoading = false;
    this.checkInstalling = false;
    this.installing = false;
    this.isFullLoadFinished = true;
    this.isPermissionLoading = false;
    if (this.athenaPollingInterval) {
      clearInterval(this.athenaPollingInterval);
      this.athenaPollingInterval = null;
    }
  }

  public setupCrisp(e: any) {
    // The Crisp plugin deletes window.$crisp before l.js loads; until l.js
    // recreates it, every push below would throw and no listener would ever
    // register. Crisp's own queue snippet: l.js drains this array on load.
    (window as any).$crisp = (window as any).$crisp || [];
    try {
      // Hide Crisp initially
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['do', 'chat:hide']);

      // Listen for when Crisp is opened
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'chat:opened', () => {
        // Emit event to hide agent FAB when Crisp opens
        EventBus.$emit('crisp-opened');
        liveChatState.noteOpened();
      }]);

      // Listen for when Crisp is closed
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'chat:closed', () => {
        // Hide Crisp and show agent FAB when Crisp is closed
        // @ts-ignore: Unreachable code error
        this.$crisp.push(['do', 'chat:hide']);
        EventBus.$emit('crisp-closed');
        liveChatState.noteClosed();
      }]);

      // AS-11647 (P0-5): a reply from the team while the chatbox is hidden
      // must not go unseen — the agent FAB and Atlas show it as unread.
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'message:received', () => {
        liveChatState.noteMessageReceived();
      }]);

      // Replies that arrived while the page was closed or reloading: on
      // session:loaded, or now if Crisp already loaded its session before
      // these listeners were registered.
      const noteUnread = () => {
        const w = window as any;
        const count = typeof w.$crisp?.get === 'function' ? Number(w.$crisp.get('chat:unread:count')) || 0 : 0;
        liveChatState.noteUnreadCount(count);
      };
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'session:loaded', noteUnread]);
      noteUnread();
    } catch (err) {
      console.warn('[App] Crisp listeners not registered', err);
    }
  }

  public showReportSideBar() {
    return [
      'templateReports',
      'customReports',
      'customReport',
      'templateReport',
      'detailReportPage',
    ].includes(this.$route.name || '');
  }

  public checkPermission() {
    return true;
    const permissions: { [key: string]: string[] } = {
      notification: [
        'notifications',
        'notificationNewPage',
      ],
    };

    for (const permission of Object.keys(permissions)) {
      if (Object.keys(this.permission.features).includes(permission)) {
        if (permissions[permission].includes(this.$route.name || '')) {
          this.currentFeature = permission;
          return this.permission.features[permission];
        }
      }
    }

    return true;
  }

  public checkIsRequiredLogin() {
    const notRequiredLoginUrls = [
      'dataFileView',
      'statusPage',
    ];
    if (notRequiredLoginUrls.includes(this.$route.name || '')) {
      this.isRequiredLogin = false;
    } else {
      this.isRequiredLogin = true;
    }
  }

  /**
   * Phase 1: Early Mixpanel SDK initialization
   *
   * Initializes the Mixpanel SDK immediately so it's ready to track events
   * even before store data is loaded. This prevents tracking errors in components
   * that load early (like SubscriptionConfirm).
   *
   * Called at the beginning of created() lifecycle hook.
   */
  private initializeMixpanelSdk(): void {
    try {
      const token = process.env.VUE_APP_MIXPANEL_TOKEN;
      if (!token) {
        console.warn('[App] Mixpanel token not found, skipping SDK initialization');
        return;
      }

      // Detect embedded mode
      const isEmbeddedMode = isEmbedded();

      // Initialize Mixpanel SDK early
      mixpanel.init(token, {
        debug: process.env.NODE_ENV !== 'production',
        ignore_dnt: process.env.NODE_ENV !== 'production', // Respect DNT in production
        disable_persistence: isEmbeddedMode
      });

    } catch (error) {
      console.warn('[App] Failed to initialize Mixpanel SDK:', error);
    }
  }

  /**
   * Phase 2: Mixpanel user identification and properties
   *
   * Called after store data is loaded to identify the user and set super properties.
   * The SDK must already be initialized via initializeMixpanelSdk() first.
   */
  private initializeMixpanel(): void {
    if (!this.store) {
      return;
    }
    // Initialize Mixpanel with safe error handling
    const initialized = MixpanelInitializer.initialize(this.store, {
      debug: process.env.NODE_ENV !== 'production'
    });
    
    if (initialized) {
      // Set super properties that will be included with every event
      MixpanelInitializer.setSuperProperties({
        app_version: this.store.merchantAppVersion,
        system_version: this.store.systemAppVersion,
        athena_enabled: this.store.enableAthena,
        store_type: this.store.storeType || 'standard',
        etl_status: this.store.etlStatus,
        is_data_ready: this.isDataReady,
        environment: process.env.NODE_ENV
      });
      
      // Track successful login
      MixpanelInitializer.trackEvent('User Login Success', {
        login_method: this.isLoggedInFromHades ? 'hades' : 'standard',
        has_slack: this.store.slackConnected,
        has_channel: this.store.channelConnected
      });
    }
  }

  public async getStoreDetail() {
    await this.store.fetch();
    // AS-11272 M2: if Kong sent an atlas-cohort store to warbler, hop to the
    // same URL with `shop=` set so the edge can route on the shop instead of
    // the stale cookie. Fails open on a missing value; one hop per 15 s.
    if (runShellGuard({
      thisApp: 'warbler',
      frontendApp: this.store.frontendApp,
      shopName: this.store.shopName,
    }).kind === 'hop') {
      return;
    }
    EventBus.$emit('store-detail-fetched');
    // Initialize permission service right after store fetch
    await this.initializePermissionService();
    localStorage.setItem('currency', this.store.information.currency);
    // Store the Shopify store name for use in other components
    if (this.store.shopName) {
      localStorage.setItem('shopifyStoreName', this.store.shopName);
    }
    if (this.store.etlStatus !== 'ready') {
      this.isDataReady = false;
    }
    this.shopifyName = this.store.shopName;

    // Init cache for OLAPs all stores which id is greater than betaStoreId
    // TODO: Need to update for all merchants
    const betaStoreId = 1;
    const isBetaStore = betaStoreId < this.store.id;

    if (isBetaStore && !this.olapsWarmedThisAppInstance) { // Check the instance flag
      try {
        // console.log('Warming OLAPs for this app instance...'); // Optional: for debugging
        this.olapsWarmedThisAppInstance = true; // Set the flag for this instance
        await this.store.warmOlaps();
      } catch (error) {
        console.error('Failed to warm OLAPs:', error);
        // Optionally, handle the error. You might choose not to set the flag
        // if it fails, allowing another attempt if getStoreDetail is called again.
      }
    } else if (isBetaStore && this.olapsWarmedThisAppInstance) {
      // console.log('OLAPs already warmed for this app instance.'); // Optional: for debugging
    }
    // End init

    if (this.store.merchantAppVersion !== this.store.systemAppVersion) {
      this.needUpgrade = true;
    }

    if (this.store.needsInventoryScope()) {
      if (this.getCanShowUpgradeVersion) {
        this.needUpgrade = true;
      }
    }

    if (this.store.passedInstallingFlow !== 'new') {
      if (
        isOnboardExitRoute(this.$route.name)
        && !shouldBypassOnboardGuard(this.$route.query, process.env.VUE_APP_ENVIRONMENT)
      ) {
        if (isEmbedded()) {
          this.$router.push({ name: 'home' });
        } else {
          window.location.assign(`https://admin.shopify.com/store/${this.store.shopName.split('.')[0]}/apps/${process.env.VUE_APP_NAME}`);
        }
      }

    } else {
      if (this.$route.name === 'SetupGuide') {
        const storeModel = new StoreModel();
        await storeModel.fetch();
        this.store = storeModel;
        // Store the Shopify store name for use in other components
        if (this.store.shopName) {
          localStorage.setItem('shopifyStoreName', this.store.shopName);
        }
      } else {
        if (!isInstallationRoute(this.$route.name)) {
          this.$router.push({ name: 'onboardPricing' });
        }
      }
    }
    try {
      if (process.env.VUE_APP_ENVIRONMENT === 'production' && window.location.hostname !== 'demo.assisty.ai' && !window.location.hostname.endsWith('.trycloudflare.com')) {
        try {
          LogRocket.identify(`${this.store.id}`, {
            name: this.store.information.myshopify_domain,
            email: this.store.information.email,
          });
        } catch {
          // skipped
        }
      }
      if (!this.isLoggedInFromHades && this.store?.config?.email) {
        // @ts-ignore: Unreachable code error
        this.$crisp.push([
          'set', 'user:email', [this.store.config.email],
        ]);
        if (this.store?.information?.name) {
          // @ts-ignore: Unreachable code error
          this.$crisp.push([
            'set', 'user:nickname', [this.store.information.name],
          ]);
        }
        // @ts-ignore: Unreachable code error
        this.$crisp.push([
          'set',
          'session:data',
          [
            [
              ['assisty_plan', this.store.currentPackage.name],
              ['assisty_plan_actived_at', this.store.currentPackage.activatedAt || this.store.currentPackage.createdAt],
              ['shopify_plan', this.store.information.plan_display_name],
              ['assisty_installed_at', this.store.installedAt],
              ['id', this.store.id],
              ['url', this.store.information.domain],
              ['email', this.store.information.email],
              ['name', this.store.information.myshopify_domain],
              ['hades_url', `https://hades.assisty.ai/stores/${this.store.id}`],
              ['is_trial', this.store.isTrial()],
              ['current_step', this.store.currentStep],
              ['store_admin_url', this.store.information.myshopify_domain.replace('.myshopify.com', '')],
            ],
          ],
        ]);
      }
    } catch (err: any) {
      // do nothing
    }
  }

  /**
   * Handle when user skips the upgrade dialog
   */
  public handleUpgradeSkipped() {
    // Mark this session as having skipped the upgrade
    this.needUpgrade = false;


    // Reload the main content to ensure components initialize properly
    this.mainRandomKey = Math.random();

    // Show a notification to inform the user about limited functionality
    EventBus.$emit('show-snackbar', {
      message: 'Some features may be limited until you upgrade the app permissions.',
      color: 'warning',
    });
  }

  /**
   * Check for pending login requests
   */
  public async checkForPendingLoginRequests() {
    if (!this.store.id) {
      return;
    }

    try {
      const loginRequestList = new LoginRequestList();
      const request = await loginRequestList.checkForPending(this.store.id);

      if (request) {
        this.pendingLoginRequest = request;
        this.showLoginRequestPopup = true;
      }
    } catch (error: any) {
      console.error('Error checking for pending login requests:', error);
    }
  }

  /**
   * Start periodic check for login requests
   */
  public startLoginRequestCheck() {
    // Check only once on app init, no interval
    this.checkForPendingLoginRequests();
  }

  /**
   * Handle when customer responds to login request
   */
  public onLoginRequestResponded(request: LoginRequestModel) {
    this.pendingLoginRequest = null;
    this.showLoginRequestPopup = false;
    // No need to check again - only check on app init
  }

  /**
   * Show snackbar notification
   */
  public showSnackbar(config: { message: string; color: string }) {
    this.appSnackbar = true;
    this.appSnackbarMessage = config.message;
    this.appSnackbarColor = config.color;
  }

  public onSnackbarActionClick(): void {
    const action = this.appSnackbarAction;
    this.appSnackbar = false;
    if (action && action.to && this.$route.path !== action.to) {
      this.$router.push(action.to).catch(() => { /* ignore duplicate nav */ });
    }
  }

  /**
   * Initialize the permission service for feature access control
   */
  public async initializePermissionService() {
    if (this.store) {
      // Set the initialization flag if not already set
      if (Vue.prototype.$permissionsInitializing === undefined) {
        Vue.prototype.$permissionsInitializing = true;
      }

      // Make store globally available for route guards immediately
      Vue.prototype.$appStore = this.store;

      try {
        // Make sure rules are loaded
        if (!this.store.rules || !this.store.rules.items || this.store.rules.items.length === 0) {
          await this.store.getRules();
        }
        // Initialize permission service
        this.permissionService = providePermission(this.store);
        this.permissionServiceInitialized = true;

        // Clear initialization flag
        Vue.prototype.$permissionsInitializing = false;

        // Emit event to notify components that permissions are ready
        EventBus.$emit('permissions-ready');
      } catch (error: any) {
        console.error('Failed to initialize permission service:', error);
        // Even on error, we should clear the initializing flag to avoid blocking navigation
        Vue.prototype.$permissionsInitializing = false;

        // Show error message to user
        EventBus.$emit('show-snackbar', {
          message: 'Could not load permissions. Some features may be unavailable.',
          color: 'error',
          timeout: 8000,
        });
      }
    }
  }

  // Add or modify beforeDestroy
  public beforeDestroy() {
    if (this.athenaPollingInterval) {
      clearInterval(this.athenaPollingInterval);
      this.athenaPollingInterval = null;
    }
    EventBus.$off('update-new-store-data');
    EventBus.$off('full-load-app');
    EventBus.$off('check-athena-finish-interval');
    // EventBus.$off("pin-menu", this.pinMenu); // Ensure this is removed if it existed
    EventBus.$off('redirect-to-google-auth');
    EventBus.$off('show-snackbar');
    EventBus.$off('clear-cache');
    EventBus.$off('permission-denied');
    EventBus.$off('check-permission');
    EventBus.$off('permissions-ready'); // This was .$on, should be .$off if it had a handler
    EventBus.$off('etl-polling-started');
    EventBus.$off('etl-polling-stopped');
    EventBus.$off('vertical-menu-pinned', this.handleVerticalMenuPinned);
    EventBus.$off('app:chatbox-layout-change', this.onChatboxLayoutChange);
    EventBus.$off('purchase-list-drawer-visible', this.onPurchaseListDrawerVisible);
    EventBus.$off(LOGOUT_REQUESTED, this.doLogout);
    if (this.offSessionEvicted) {
      this.offSessionEvicted();
      this.offSessionEvicted = null;
    }
  }

  public onPurchaseListDrawerVisible(open: boolean): void {
    this.isPurchaseListDrawerOpen = open;
  }

  private async checkLogin() {
    this.loading = true;
    if (Object.keys(this.$route.query).includes('hades')) {
      this.isLoggedInFromHades = true;
      localStorage.setItem('loggedInFromHades', 'true');
      let res: any;
      try {
        res = await get(`/hades/verify`, { token: this.$route.query.hades });
        const token = res.token;
        if (token) {
          setIdentityToken(token, res.store.shop_name);
          this.isLogined = true;
          const query = Object.assign({}, this.$route.query);
          delete query.hades;
          this.$router.replace({ query });
          await this.getStoreDetail();
          this.error = false;
        } else {
          this.isLogined = false;
        }
      } catch (err: any) {
        if (err.response.status === 401) {
          removeIdentityToken();
        }
        this.isLogined = false;
      }
    }

    if (verifyIdentityToken()) {
      // A shell hop names the store in assisty_shop instead of shop (AS-11379).
      const queryShop = this.$route.query.shop || this.$route.query.assisty_shop;
      if (queryShop) {
        if (localStorage.getItem('shopify_store') !== queryShop) {
          this.isLogined = false;
          removeIdentityToken();
          return;
        }
      }
      try {
        const verifyResult: any = await get('/token/verify');
        if (verifyResult.token) {
          setIdentityToken(verifyResult.token, verifyResult.store.shop_name);
          if (this.store?.config?.contactEmail) {
            localStorage.setItem('current_loggedin_email', this.store.config.contactEmail);
          }
        }
        this.isLogined = true;
        this.removeShopifyQueries();
        await this.getStoreDetail();
      } catch (err: any) {
        if (err.response?.status === 401) {
          removeIdentityToken();
        }
        this.isLogined = false;
      }
    } else {
      this.isLogined = false;
    }
    if (this.isLogined) {
      //if (!this.isLoggedInFromHades) {
        // Legacy code - kept commented for reference
      //}

      const redirectUri = localStorage.getItem('redirect_uri');
      if (redirectUri) {
        localStorage.removeItem('redirect_uri');
        if (redirectUri !== this.$route.fullPath) {
          this.$router.push(redirectUri).catch((err: any) => {
            if (err?.name !== 'NavigationDuplicated') throw err;
          });
        }
      }

      return;
    } else {
      if (this.$route.path !== '/') {
        this.referenceRoute = this.$route;
        // this.$router.push({ name: 'home', query: this.$route.query });
      }
    }
    this.loading = false;
  }

  private removeShopifyQueries() {
    const original = this.$route.query;
    const shopifyKeys = ['hmac', 'host', 'session', 'shop', 'timestamp', 'assisty_shop'];
    const hasShopifyQuery = shopifyKeys.some((key) => key in original);
    if (!hasShopifyQuery) {
      return;
    }
    const query = Object.assign({}, original);
    shopifyKeys.forEach((key) => delete query[key]);
    this.$router.replace({ query }).catch((err: any) => {
      if (err?.name !== 'NavigationDuplicated') {
        throw err;
      }
    });
  }

  private async createStore() {
    this.creating = true;
    const query = this.$route.query;
    const data: { [key: string]: any } = {};
    for (const key of Object.keys(query)) {
      data[key] = query[key];
    }
    try {
      const res: any = await get(`/shopify/callback`, data);
      const token = res.token;
      if (token) {
        setIdentityToken(token, res.store.shop_name);
        const adminUrl = `https://admin.shopify.com/store/${data.shop.split('.')[0]}/apps/${process.env.VUE_APP_NAME}`;
        if (isEmbedded()) {
          redirectExternal(adminUrl);
        } else {
          window.location.assign(adminUrl);
        }
        this.isLogined = true;
        this.error = false;
        this.installing = false;
        this.creating = false;
      }
    } catch (e: any) {
      this.error = true; this.errorMessage = e.response.data.message; this.creating = false;
      return;
    }
  }

  private async getClientIP() {
    try {
      const axios = require('axios').default;
      const options = {
        method: 'get',
        url: 'https://api.ipify.org?format=json',
      };
      const ip = await axios.request(options).then((response: any) => {
        return response.data.ip;
      });
      return ip;
    } catch (error: any) {
      return '';
    }
  }

  private async checkInstallation() {
    this.error = false;
    this.installing = false;
    this.checkInstalling = true;
    // Managed-install path (VUE_APP_SHOPIFY_SESSION_AUTH): App Bridge session
    // token -> POST /shopify/session -> Assisty JWT. Any failure falls through
    // to the legacy hmac flow below unchanged.
    if (await attemptShopifySessionAuth()) {
      try {
        // Hydrate the store before clearing the loading flags — the legacy paths
        // (checkLogin, install) both load the store here; without it the menu /
        // StoresSwitcher mounts with an empty store and crashes on store.id.
        // Guarded: if hydration fails after a valid session exchange, fall through
        // to the legacy path rather than leaving the app stuck in a loading state.
        await this.getStoreDetail();
        this.isLogined = true;
        this.loading = false;
        this.checkInstalling = false;
        this.removeShopifyQueries();
        return;
      } catch (e) {
        this.isLogined = false;
      }
    }
    await this.checkLogin();
    if (this.isLogined) {
      this.error = false;
      this.loading = false;
      this.installing = false;
      this.checkInstalling = false;
      return;
    }
    const query = this.$route.query;
    const data: { [key: string]: any } = {};
    for (const key of Object.keys(query)) {
      data[key] = query[key];
    }
    try {
      const res: any = await get(`/shopify`, data);
      const installUrl = res.install_url;
      const token = res.token;
      if (installUrl) {
        redirectExternal(installUrl);
        this.error = false;
        this.installing = true;
      }
      if (token) {
        setIdentityToken(token, res.store.shop_name);
        this.isLogined = true;
        this.removeShopifyQueries();
        await this.getStoreDetail();
        if (this.referenceRoute) {
          this.$router.push(this.referenceRoute);
          this.removeShopifyQueries();
        }
        this.error = false;
        this.installing = false;
      }
    } catch (e) {
      this.error = true;
      if (getIdentityToken()) {
        this.errorMessage = 'There are some error while installing. Please try again later';
      } else {
        this.errorMessage = 'You are not logged in yet. Please go to Shopify Store\'s Apps to access Assisty.';
      }
      this.installing = false;
    }
    this.checkInstalling = false;
  }

  @Watch('store.isAthenaReady')
  public onAthenaReadyChange(newVal: boolean, oldVal: boolean) {
    if (newVal && !oldVal && this.$route.name === 'dataImporting') {
      this.showDataReadyModal = true;
      const eventHandler = new EventHandler({ store: this.store });
      eventHandler.track('Data Ready Modal Shown', {
        store: this.store.shopName,
      });
    }
  }

  public onDataReadyDismiss() {
    this.showDataReadyModal = false;
    if (this.$route.path !== '/') {
      this.$router.push('/');
    }
  }

  @Watch('$route', { immediate: true, deep: false })
  private async onUrlChange(newVal: any) {
    this.isCallbackRequest = this.$route.name === 'callback';
    const embedded = isEmbedded();
    const fullPath = this.$route.fullPath;

    if (embedded) {
      // Drop Shopify auth params so the persisted URL is clean.
      const SHOPIFY_AUTH_PARAMS = new Set([
        'embedded', 'hmac', 'id_token', 'shop', 'timestamp',
        'host', 'locale', 'session', 'link_source',
      ]);
      const [pathPart, queryPart] = fullPath.split('?', 2);
      const params = new URLSearchParams(queryPart || '');
      for (const key of Array.from(params.keys())) {
        if (SHOPIFY_AUTH_PARAMS.has(key)) params.delete(key);
      }
      const cleanQuery = params.toString();
      const cleanPath = pathPart + (cleanQuery ? '?' + cleanQuery : '');

      // Clean the iframe URL
      if (cleanPath !== window.location.pathname + window.location.search) {
        window.history.replaceState({}, '', cleanPath);
      }

      // Persist per-tab so reload restores the route (DISABLED).
      // Removed because /subscription/confirm?charge_id=... could be
      // restored after a successful confirmation, creating a
      // reconfirmation loop. See created() for the matching restore
      // block, also disabled.
      // const isTransactional =
      //   pathPart.startsWith('/subscription/confirm') ||
      //   pathPart.startsWith('/oauth2callback') ||
      //   pathPart.startsWith('/slack_callback');
      // if (this.$route.name !== 'callback' && !isTransactional && this.tabId) {
      //   try {
      //     localStorage.setItem(
      //       `warbler_last_route_${this.tabId}`,
      //       cleanPath,
      //     );
      //   } catch {
      //     // no-op: localStorage may be blocked (third-party cookies)
      //   }
      // }
    }
    if (this.isLogined && this.isRequiredLogin) {
      try {
        if (!this.isLoggedInFromHades) {
          if (this.$route.name !== 'customReport'
            && this.$route.name !== 'templateReport'
          ) {
            const eventHandler = new EventHandler({
              store: this.store,
            });
            eventHandler.track(`view ${this.$route.name} page`);
            this.store.sendEvent('VIEW_PAGE', {
              name: this.$route.name,
              path: this.$route.path,
            });
          }
        }
      } catch (e) {
        // ignore this
      }
      this.getStoreDetail();
    }
  }

  private clearCache() {
    for (const key of Object.keys(localStorage)) {
      if (key.split('_')[0] === 'cache') {
        localStorage.removeItem(key);
      }
    }
  }
}
